import { Context, getRollbarFromContext } from "@rollbar/react";
import { Alert } from "@material-ui/lab";
import axios from "axios";
import isEmpty from "lodash.isempty";
import { withSnackbar } from "notistack";
import { errorStackState } from "pages/CustomerBooking";
import React, { useEffect } from "react";
import { toast } from "react-hot-toast";
import { matchPath, useHistory, useRouteMatch } from "react-router-dom";
import { useRecoilState } from "recoil";
import { serializeError, deserializeError } from 'serialize-error';
import { toasterror } from "util/toasterror";


const API_URL = process.env.REACT_APP_WEB_ADMIN_URL;
class ErrorBoundary extends React.Component {
  state = {
    hasErrored: false,
    error: null,
    code: null,
    message: null,
  };

  checkSafeFailUrl = (requrl) => {
    if (isEmpty(requrl)) return false;
    return [
      "/api/2/appointments/:id/extend",
      "/api/1/clients/me/promos",
      "/api/1/whitelabel/bookings/locations/update",
      "/api/1/log",
      "/api/1/logs"
    ].some((r) => {
      const matchUrl = matchPath(requrl, {
        path: API_URL + r,
        exact: true,
        strict: false,
      });
      return !!matchUrl;
    });
  };

  rejectHandler = (event) => {
    event.preventDefault();
    const isBooking = this?.props?.location?.pathname?.startsWith("/bookings");
    const match = this.checkSafeFailUrl(event?.reason?.request?.responseURL);
    if (!match) {
      const message = event?.reason?.response?.data?.message?.en || event?.reason?.message || event?.reason
      const code = this.props.location
      if (isBooking) {
        // toasterror(message, {error:event?.reason})
        // this.rollbar.error(event?.reason)
        this?.props?.setErrors([...this?.props?.errors, {type: "App Error", message, error:serializeError(event?.reason)}])
        return
      } else {
        this.props.enqueueSnackbar(
          {
            message,
            code,
            type: "App Error",
          },
          {
            location,
            //persist: true,
            variant: "error",
          }
        );
      }
    }
  };

  componentDidMount() {
    const isBooking = this?.props?.location?.pathname?.startsWith("/bookings") || this?.props?.location?.pathname?.startsWith("/createbooking");
    // this.rollbar = getRollbarFromContext(this.context)
    window.onError = (err) => {
      if (isBooking) {
        // toasterror(err?.message, {error: err})
        // this.rollbar.error(err)
        this?.props?.setErrors([...this?.props?.errors, { type: "App Error", message: err?.message, error: serializeError(err) }])
        return
      }
      this.props.enqueueSnackbar(
        { message: err.message, code: this.props.location, type: "JSWindow" },
        {
          location: this.props.location,
          //persist: true,
          variant: "error",
        }
      );
    };
    window.addEventListener("unhandledrejection", this.rejectHandler);
    this.requestInterceptor = axios.interceptors.request.use((req) => {
      this.setState({ hasErrored: false });
      return req;
    });

    this.responseInterceptor = axios.interceptors.response.use(
      (res) => res,
      (error) => {
        console.log(error?.request);
        let jsonMessage = "";
        const errorDetails = `E${error?.request?.status}:${error?.response?.config?.method}:${(error?.request?.responseURL).replace(API_URL, "")}`;
        // this.rollbar.error(error, {response: error?.response, request: error?.request})
        try {
          jsonMessage = JSON.parse(error?.request?.response);
        } catch (em) {
          jsonMessage = `Error ${error?.request?.status}: ${error.request.responseURL}`;
        }
        const message =
          jsonMessage?.details ?? jsonMessage?.message?.en ?? 
          (typeof jsonMessage === 'string' ? jsonMessage : JSON.stringify(jsonMessage, null, 2));
        this.setState({
          hasErrored: true,
          error,
          code: error?.request?.status,
          message: message,
        });



        if (!this.checkSafeFailUrl(error?.request?.responseURL)) {
          if (isBooking) {
            if (error.response.data.details === 'Invalid booking id') {
              this?.props?.bookingNotFound()
            }
            // toasterror(message ?? error?.message, {error})
            // this.rollbar.error(error)
            this?.props?.setErrors([...this?.props?.errors, { type: "Server Error", message, error: serializeError(error)}])
            throw error
            return            
          }
          this.props.enqueueSnackbar(
            {
              message: message ?? error.message,
              code: this.props.location,
              type: "Server",
            },
            {
              location: this.props.location,
              //persist: true,
              variant: "error",
            }
          );
        }
        throw error;
      }
    );
  }

  componentWillUnmount() {
    axios.interceptors.request.eject(this.requestInterceptor);
    axios.interceptors.response.eject(this.responseInterceptor);
    window.removeEventListener("unhandledrejection", this.rejectHandler);
  }

  static getDerivedStateFromError(error) {
    return {
      hasErrored: true,
      error,
    };
  }

  componentDidCatch(error, info) {
    const isBooking = this?.props?.location?.pathname?.startsWith("/bookings");
    console.log(`Cause: ${error}.\nStackTrace: ${info.componentStack}`);
    if (isBooking) {
      // toasterror(error?.message, {error})
      // this.rollbar.error(error)
      this?.props?.setErrors([...this?.props?.errors, { type: "App Error", error: serializeError(error) }])
      return
    }
    this.props.enqueueSnackbar(
      { message: error.message, code: this.props.location, type: "React" },
      {
        variant: "error",
      }
    );
  }

  render() {
    // if (this.state.hasErrored) {
    //   return (
    //     <Alert severity="error">
    //       {this.state?.message || this.state?.error?.message}
    //     </Alert>
    //   );
    // }
    return this.props.children;
  }
}

const ConnectedErrorBoundary = withSnackbar(ErrorBoundary);


const ErrorBoundaryWrapper = (props) => {
  const [errorState, setErrorState] = useRecoilState(errorStackState);
  const history = useHistory();
  const isRouteSvc = useRouteMatch({
    path: [
      '/bookings/login/:serviceId',
      '/bookings/oops/:serviceId',
      '/bookings/:serviceId/code/:code',
      '/bookings/:serviceId/',
    ],
    strict: false,
    sensitive: true
  })


  useEffect(
    () => {
      const groupedMessage = errorState.reduce((acc, curr) => {
        if (!acc[curr.type]) {
          acc[curr.type] = [];
        }
        const fusked = JSON.stringify(curr, (k,v) => {
          return ['X-Auth-Token'].includes(k) ? (isEmpty(v) ? `empty` : '***') : v
        }, 2
        )
        acc[curr.type].push(JSON.parse(fusked));
        return acc;
      }, {});
      Object.keys(groupedMessage).forEach((key) => {
        const message = key
        toasterror(message, {error: groupedMessage[key], id:key})
      })
    }, [errorState]
  );
  return (
    <ConnectedErrorBoundary {...props}
      bookingNotFound={() => {
        
      }}
      setErrors={(errors) => {
        setErrorState(errors);
      }}
      errors={errorState}
    >
      {props.children}
    </ConnectedErrorBoundary>
  );
};

export default ErrorBoundaryWrapper;
